import { FieldResponse } from '@flowwai-frontend/bps-officer-web/graphql'
import { Sales_Crm_Web_CustomFieldResponseType_FragmentFragment } from '@flowwai-frontend/bps-officer-web/graphql-docs'
import {
   AttachmentModel,
   GeoLocationDetails,
   RelatedRecord,
   StageResponse,
   UnionFieldModelType,
   UnionFieldResponse,
   UnionFieldResponseType,
   User
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import { convertPipeLineTypeToPipeLineTypeEnum } from '../factories/pipeline'

export const getFieldResponsesBasedOnFieldType = (
   data: any, //FIXME: replace this any with Sales_Crm_Web_CustomFieldResponseType_FragmentFragment
   createOrUpdateUser?: (user: User) => void
): UnionFieldResponseType => {
   if (!data) return null

   const response = data as Sales_Crm_Web_CustomFieldResponseType_FragmentFragment

   switch (response.__typename) {
      case 'GQLStringType':
         return response.stringValue
      case 'GQLListStringType':
         return response.values
      case 'GQLDateTimeType':
         return response.dateTime ? new Date(response.dateTime) : undefined
      case 'GQLDateType':
         return response.date ? new Date(response.date) : undefined
      case 'GQLTimeType':
         return response.timeValue
      case 'GQLFloatType':
         return response.floatValue?.toString()
      case 'GQLIntType':
         return response.integerValue?.toString()
      case 'PhoneNumberResponse':
         return {
            dialCode: response.dialCode,
            iso2CountryCode: response.iso2CountryCode as string,
            phoneNumber: response.phoneNumber
         }
      case 'Stage':
         return {
            stageId: response.stageId,
            name: response.name,
            order: response.order,
            color: response.color ?? undefined,
            overviewFieldsConfig: data.overviewFieldsConfig,
            quickEditFields: data.quickEditFields
         } as StageResponse
      case 'StageIsDeleted':
      case 'StageNotFound':
         return null

      case 'BaseUser': {
         const user: User = {
            userId: response.userId,
            email: response.email,
            name: response.userName,
            profilePicUrl: response.profilePicUrl,
            attendance: response.attendance
         }
         createOrUpdateUser && createOrUpdateUser(user)
         return user
      }

      case 'FileUploaderFieldResponse': {
         return response.urlResponses
            ? response.urlResponses.map(urlResponse =>
                 AttachmentModel.createAttachmentModel({
                    attachmentId: urlResponse.fileId,
                    name: urlResponse.name,
                    mimeType: urlResponse.mimeType,
                    sizeInBytes: urlResponse.sizeInBytes,
                    url: urlResponse.url,
                    preSignedUrl: urlResponse.url //Need to remove this key from the model
                 })
              )
            : []
      }
      case 'RelationFieldResponse': {
         const relatedRecords: RelatedRecord[] = response.pipelineItems.map(
            record => ({
               pipelineItemType: convertPipeLineTypeToPipeLineTypeEnum(
                  record.pipelineItemType
               ),
               pipelineItemId: record.pipelineItemId,
               pipelineId: record.pipelineId,
               name: record.name ?? '',
               hasPermission: record.hasPermission,
               isDeleted: record.isDeleted
            })
         )
         return relatedRecords
      }
      case 'GeoLocationFieldResponse': {
         const geoLocationDetails: GeoLocationDetails = {
            latitude: response.latitude,
            longitude: response.longitude
         }
         return geoLocationDetails
      }

      case 'GeoShapeFieldResponse': {
         const { geoPoints: locationPoints } = response
         const geoLocationDetails: GeoLocationDetails[] = locationPoints.map(
            point => ({
               latitude: point.latitude,
               longitude: point.longitude
            })
         )

         return geoLocationDetails
      }
      case 'ShortTextFieldResponse': {
         return response.value
            ? {
                 prefix: response.prefix ?? undefined,
                 value: response.value,
                 suffix: response.suffix ?? undefined
              }
            : null
      }

      case 'BasePipeline':
      case 'PipelineItemOverviewDetails':
      default:
         throw new Error('Unhandled Field Response Type')
   }
}

export const getTransformedFieldResponse = (
   fieldModel: UnionFieldModelType,
   fieldResponse: FieldResponse,
   createOrUpdateUser?: (user: User) => void
): UnionFieldResponse => ({
   fieldId: fieldResponse.fieldId,
   fieldResponseId: fieldResponse.fieldResponseId,
   fieldType: fieldModel.fieldType,
   response: fieldResponse.response
      ? (getFieldResponsesBasedOnFieldType(
           fieldResponse.response,
           createOrUpdateUser
        ) as any)
      : null
})
